// components/invoices/InvoicePdfViewer.tsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Backdrop, Box, CircularProgress, Paper, Snackbar } from '@mui/material';

import { getInvoiceInfo } from '../../../../../features/salesOrder/services';
import { ShowAlertState } from '../../../../../features/common/types';
import { defaultAlertState } from '../../../../../features/common/constants';
import { extractErrorMessage } from '../../../../../features/quotation/helpers';
import { useTheme } from '@mui/styles';
import { base64ToBlob } from '../../../../../features/common/helpers';

type RouteParams = {
	invoiceId?: string;
};

const InvoicePdfViewer: React.FC = () => {
	const { invoiceId } = useParams<RouteParams>();

	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const theme = useTheme();

	// Cambiá esta función según tu endpoint real
	const fetchInvoicePdf = async (id: string): Promise<void> => {
		setLoading(true);
		setAlert(defaultAlertState);

		try {
			const { data } = await getInvoiceInfo(Number(id));

			// liberar URL anterior si había
			setPdfUrl((prev) => {
				if (prev) URL.revokeObjectURL(prev);
				return prev;
			});

			const blob = base64ToBlob(data.base64, 'application/pdf');
			const url = URL.createObjectURL(blob);
			setPdfUrl(url);
		} catch (error: any) {
			console.error(error);
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Error al obtener la información de la factura'),
			});
		} finally {
			setLoading(false);
		}
	};

	// Cargar al montar y cada vez que cambie el invoiceId
	useEffect(() => {
		if (invoiceId && !isNaN(Number(invoiceId))) {
			fetchInvoicePdf(invoiceId);
		} else {
			setAlert({
				show: true,
				severity: 'error',
				message: 'No se especificó un ID válido de factura en la URL',
			});
		}

		// cleanup: liberar URL del blob al desmontar
		return () => {
			if (pdfUrl) {
				URL.revokeObjectURL(pdfUrl);
			}
		};
	}, [invoiceId]);

	return (
		<>
			<Backdrop
				sx={{
					zIndex: theme.zIndex.modal + 1,
					color: '#fff',
				}}
				open={loading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Box
				sx={{
					height: '100vh',
					bgcolor: 'background.default',
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
				}}
			>
				{/* Contenido principal */}
				<Box sx={{ flex: 1, minHeight: 0 }}>
					{!loading && pdfUrl && (
						<Paper
							variant="outlined"
							sx={{
								height: '100%',
								overflow: 'hidden',
							}}
						>
							<iframe
								title={`Factura ${invoiceId}`}
								src={pdfUrl}
								style={{
									border: 'none',
									width: '100%',
									height: '100%',
								}}
							/>
						</Paper>
					)}
				</Box>
			</Box>
			<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default InvoicePdfViewer;
